/* eslint-disable @angular-eslint/component-selector, @angular-eslint/component-class-suffix, @angular-eslint/no-input-rename */
import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  input,
  numberAttribute,
} from '@angular/core';
import { hlm } from '@shared/utils/hlm';
import type { ClassValue } from 'clsx';
import { NgxSonnerToaster, type ToasterProps } from 'ngx-sonner';

@Component({
  selector: 'hlm-toaster',
  imports: [NgxSonnerToaster],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host ::ng-deep [data-sonner-toast].hlm-toast-glass::before {
        content: '';
        position: absolute;
        inset: 0;
        z-index: -1;
        width: 100%;
        height: 100%;
        border-radius: inherit;
        pointer-events: none;
        backdrop-filter: blur(32px) saturate(180%);
      }
    `,
  ],
  template: `
    <ngx-sonner-toaster
      [class]="_computedClass()"
      [invert]="invert()"
      [theme]="theme()"
      [position]="position()"
      [hotKey]="hotKey()"
      [richColors]="richColors()"
      [expand]="expand()"
      [duration]="duration()"
      [visibleToasts]="visibleToasts()"
      [closeButton]="closeButton()"
      [toastOptions]="toastOptions()"
      [offset]="offset()"
      [dir]="dir()"
      [style]="userStyle()"
    />
  `,
})
export class HlmToaster {
  public readonly invert = input<ToasterProps['invert'], boolean | string>(false, {
    transform: booleanAttribute,
  });
  public readonly theme = input<ToasterProps['theme']>('light');
  public readonly position = input<ToasterProps['position']>('bottom-right');
  public readonly hotKey = input<ToasterProps['hotkey']>(['altKey', 'KeyT']);
  public readonly richColors = input<ToasterProps['richColors'], boolean | string>(false, {
    transform: booleanAttribute,
  });
  public readonly expand = input<ToasterProps['expand'], boolean | string>(false, {
    transform: booleanAttribute,
  });
  public readonly duration = input<ToasterProps['duration'], number | string>(4000, {
    transform: numberAttribute,
  });
  public readonly visibleToasts = input<ToasterProps['visibleToasts'], number | string>(3, {
    transform: numberAttribute,
  });
  public readonly closeButton = input<ToasterProps['closeButton'], boolean | string>(false, {
    transform: booleanAttribute,
  });
  public readonly toastOptions = input<ToasterProps['toastOptions']>({
    unstyled: true,
    class:
      'hlm-toast-glass pointer-events-auto relative overflow-hidden rounded-2xl p-4 text-slate-900 border border-white/30 bg-white/40 shadow-lg shadow-black/10 ring-1 ring-white/20 backdrop-blur-xl dark:border-white/10 dark:bg-zinc-800/40 dark:text-slate-50 dark:shadow-black/30 dark:ring-white/10',
    style: {
      background: 'transparent',
    },
    classes: {
      toast: 'relative flex items-start gap-3 px-4 py-3 pr-10',
      title: 'mt-0.5 text-sm font-semibold flex items-center gap-2',
      description: 'text-sm leading-relaxed',
      loader: 'bg-zinc-500',
      closeButton:
        'absolute right-3 top-3 rounded p-1 text-slate-500 transition hover:bg-black/5 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-white',
      default: '',
    },
  });
  public readonly offset = input<ToasterProps['offset']>(null);
  public readonly dir = input<ToasterProps['dir']>('auto');
  public readonly userClass = input<ClassValue>('', { alias: 'class' });
  public readonly userStyle = input<Record<string, string>>(
    {
      '--normal-bg': 'var(--popover)',
      '--normal-text': 'var(--popover-foreground)',
      '--normal-border': 'var(--border)',
      '--border-radius': 'var(--radius)',
    },
    { alias: 'style' },
  );

  protected readonly _computedClass = computed(() => hlm('toaster group', this.userClass()));
}
